import React, { useState } from 'react';
import { QRDesign, QRHealthReport } from '@/types/qr';
import { ChevronDown, ChevronUp, Cpu, Printer, Shield, Maximize2 } from 'lucide-react';

interface QRTechnicalDetailsProps {
  rawText: string;
  design: QRDesign;
  health: QRHealthReport;
}

export const QRTechnicalDetails: React.FC<QRTechnicalDetailsProps> = ({
  rawText,
  design,
  health
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const moduleSizeEstimateMm = Math.max(25, health.estimatedVersion * 1.5 + 20);
  const sizeInches = (moduleSizeEstimateMm / 25.4).toFixed(2);

  return (
    <div className="w-full rounded-2xl bg-card border border-border overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-[14px] font-normal text-foreground hover:bg-muted transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-primary" />
          <span>Technical Specifications & Print Metrics</span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {isOpen && (
        <div className="p-4 pt-2 border-t border-border grid grid-cols-2 sm:grid-cols-3 gap-3 text-[14px]">
          <div className="p-3 rounded-xl bg-muted border border-border">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1 text-[12px]">
              <Shield className="h-3.5 w-3.5 text-primary" />
              <span>Error Correction</span>
            </div>
            <p className="font-mono font-medium text-foreground text-[14px]">
              Level {design.errorCorrection} ({design.errorCorrection === 'L' ? '~7%' : design.errorCorrection === 'M' ? '~15%' : design.errorCorrection === 'Q' ? '~25%' : '~30%'})
            </p>
          </div>

          <div className="p-3 rounded-xl bg-muted border border-border">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1 text-[12px]">
              <Maximize2 className="h-3.5 w-3.5 text-primary" />
              <span>Symbol Version</span>
            </div>
            <p className="font-mono font-medium text-foreground text-[14px]">
              v{health.estimatedVersion} ({(health.estimatedVersion - 1) * 4 + 21}x{(health.estimatedVersion - 1) * 4 + 21})
            </p>
          </div>

          <div className="p-3 rounded-xl bg-muted border border-border">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1 text-[12px]">
              <Printer className="h-3.5 w-3.5 text-success" />
              <span>Rec. Print Size</span>
            </div>
            <p className="font-mono font-medium text-foreground text-[14px]">
              &ge; {moduleSizeEstimateMm.toFixed(0)}mm ({sizeInches}&quot;)
            </p>
          </div>

          <div className="p-3 rounded-xl bg-muted border border-border col-span-2 sm:col-span-3">
            <span className="text-[12px] text-muted-foreground block mb-1">Encoded Content</span>
            <p className="font-mono text-[12px] text-foreground break-all bg-card p-2 rounded-lg border border-border select-all">
              {rawText || 'Empty payload'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
